/**
 * Client binding for `generateAiVisuel` + hook.
 *
 * Same lifecycle as `useAiPotentiel` :
 *   - Subscribe to `/users/{uid}/fiches/{id}/ai/visuel` for live pickup.
 *   - Fire the callable on mount; server decides cache-hit vs regen via
 *     `inputsHash`.
 *
 * Like the other IA hooks, "ready" with `unavailableReason` set is a
 * legitimate factual answer (pas de geocode, IGN down, etc.), pas un erreur.
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot, type DocumentData } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";

// ─── Doc shape (keep in sync with functions/src/generateAiVisuel.ts) ─────
export type AiVisuelFeatures = {
  pool: boolean | null;
  garden: boolean | null;
  solarPanels: boolean | null;
  terrace: boolean | null;
  annexBuilding: boolean | null;
  parking: boolean | null;
};

export type AiVisuelSource = {
  kind: "ign-ortho" | "google-streetview";
  url: string | null;
  lat: number | null;
  lng: number | null;
  heading: number | null;
};

export type AiVisuel = {
  version: 1;
  architecturalStyle: string | null;
  apparentState: "neuf" | "correct" | "à rafraîchir" | "à rénover" | null;
  features: AiVisuelFeatures;
  orientation: string | null;
  mitoyennete:
    | "isolée"
    | "mitoyenne droite"
    | "mitoyenne gauche"
    | "mitoyenne droite et gauche"
    | null;
  paragraph: string;
  sources: {
    orthophoto: AiVisuelSource;
    streetView0: AiVisuelSource | null;
    streetView180: AiVisuelSource | null;
    streetViewAvailable: boolean;
  };
  unavailableReason: string | null;
  generatedAt: string;
  model: string;
  inputsHash: string;
  pdfInclude: boolean;
};

const callGenerate = httpsCallable<{ ficheId: string }, AiVisuel>(
  functions,
  "generateAiVisuel",
);

// ─── Hook ────────────────────────────────────────────────────────────────
type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: AiVisuel; fromServer: boolean }
  | { status: "error"; message: string };

export function useAiVisuel(ficheId: string | null): State {
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (!ficheId) {
      setState({ status: "idle" });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    const ref = doc(
      db,
      "users",
      user.uid,
      "fiches",
      ficheId,
      "ai",
      "visuel",
    );
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        if (!snap.exists()) return;
        const parsed = parseAiVisuel(snap.data());
        if (parsed) {
          setState({ status: "ready", data: parsed, fromServer: false });
        }
      },
      (err) => {
        console.warn("[ai/visuel] snapshot error", err);
      },
    );

    callGenerate({ ficheId })
      .then((res) => {
        if (cancelled) return;
        setState({
          status: "ready",
          data: res.data as AiVisuel,
          fromServer: true,
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState((prev) =>
          prev.status === "ready"
            ? prev
            : { status: "error", message: extractMsg(e) },
        );
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [ficheId]);

  return state;
}

// ─── Build the Street View Static URL côté client ─────────────────────────
// On NE stocke PAS la clé serveur dans Firestore — on rebuild l'URL ici avec
// la clé publique restreinte par référent HTTP (VITE_GOOGLE_MAPS_KEY).
const STREET_VIEW_STATIC_URL =
  "https://maps.googleapis.com/maps/api/streetview";

export function buildStreetViewClientUrl(args: {
  lat: number;
  lng: number;
  heading: number;
  width?: number;
  height?: number;
}): string | null {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
  if (!apiKey) return null;
  const params = new URLSearchParams({
    size: `${args.width ?? 600}x${args.height ?? 400}`,
    location: `${args.lat},${args.lng}`,
    heading: String(args.heading),
    pitch: "0",
    fov: "90",
    source: "outdoor",
    key: apiKey,
  });
  return `${STREET_VIEW_STATIC_URL}?${params.toString()}`;
}

// ─── Parse helper ────────────────────────────────────────────────────────
function parseAiVisuel(raw: DocumentData): AiVisuel | null {
  if (!raw || raw.version !== 1) return null;

  const featRaw = (raw.features ?? {}) as Record<string, unknown>;
  const features: AiVisuelFeatures = {
    pool: tribool(featRaw.pool),
    garden: tribool(featRaw.garden),
    solarPanels: tribool(featRaw.solarPanels),
    terrace: tribool(featRaw.terrace),
    annexBuilding: tribool(featRaw.annexBuilding),
    parking: tribool(featRaw.parking),
  };

  const srcRaw = (raw.sources ?? {}) as Record<string, unknown>;
  const ortho = parseSource(srcRaw.orthophoto);
  const sv0 = parseSource(srcRaw.streetView0);
  const sv180 = parseSource(srcRaw.streetView180);

  return {
    version: 1,
    architecturalStyle: str(raw.architecturalStyle),
    apparentState: parseApparentState(raw.apparentState),
    features,
    orientation: str(raw.orientation),
    mitoyennete: parseMitoyennete(raw.mitoyennete),
    paragraph: typeof raw.paragraph === "string" ? raw.paragraph : "",
    sources: {
      orthophoto: ortho ?? {
        kind: "ign-ortho",
        url: null,
        lat: null,
        lng: null,
        heading: null,
      },
      streetView0: sv0,
      streetView180: sv180,
      streetViewAvailable: Boolean(srcRaw.streetViewAvailable),
    },
    unavailableReason:
      typeof raw.unavailableReason === "string" &&
      raw.unavailableReason.length > 0
        ? raw.unavailableReason
        : null,
    generatedAt: typeof raw.generatedAt === "string" ? raw.generatedAt : "",
    model: typeof raw.model === "string" ? raw.model : "",
    inputsHash: typeof raw.inputsHash === "string" ? raw.inputsHash : "",
    pdfInclude: Boolean(raw.pdfInclude),
  };
}

function parseSource(v: unknown): AiVisuelSource | null {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  const kind = r.kind === "ign-ortho" || r.kind === "google-streetview" ? r.kind : null;
  if (!kind) return null;
  return {
    kind,
    url: str(r.url),
    lat: num(r.lat),
    lng: num(r.lng),
    heading: num(r.heading),
  };
}

function parseApparentState(v: unknown): AiVisuel["apparentState"] {
  if (
    v === "neuf" ||
    v === "correct" ||
    v === "à rafraîchir" ||
    v === "à rénover"
  )
    return v;
  return null;
}

function parseMitoyennete(v: unknown): AiVisuel["mitoyennete"] {
  if (
    v === "isolée" ||
    v === "mitoyenne droite" ||
    v === "mitoyenne gauche" ||
    v === "mitoyenne droite et gauche"
  )
    return v;
  return null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function tribool(v: unknown): boolean | null {
  return typeof v === "boolean" ? v : null;
}
function extractMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "object" && e && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return String(e);
}
